import AppComponent from '../base/app-component.ts';

/**
 * wc-passkey-login — Bottone login via WebAuthn/passkey.
 *
 * Attributi:
 *   has-passkeys  "true"|"false"  — indica se l'utente ha passkey registrate
 *
 * Se il browser non supporta WebAuthn o has-passkeys e' false,
 * il componente resta vuoto (non renderizza nulla).
 */
export class PasskeyLogin extends AppComponent {
  static override componentName = 'wc-passkey-login';

  protected override get templateId() { return 'passkey-login'; }

  private get hasPasskeys(): boolean {
    return this.attributeString('has-passkeys') === 'true';
  }

  /** Renderizza solo se il browser supporta WebAuthn e ci sono passkey. */
  protected override onConnected(): void {
    if (!window.PublicKeyCredential || !this.hasPasskeys) {
      // Nessun rendering: il componente resta invisibile
      return;
    }
  }

  protected override render(): void {
    if (!window.PublicKeyCredential || !this.hasPasskeys) return;

    super.render();
  }

  protected override afterRender(): void {
    this.delegate.on<MouseEvent>('click', '[data-action="login-passkey"]', () => {
      this.handleLogin();
    });
  }

  // --- Login passkey flow ---

  private async handleLogin(): Promise<void> {
    const btn = this.$('[data-action="login-passkey"]') as HTMLButtonElement | null;
    const errorDiv = this.$('[data-passkey-error]');
    if (!btn) return;

    // Nascondi errori precedenti
    errorDiv?.classList.add('d-none');

    // Disabilita bottone con spinner
    btn.disabled = true;
    const originalHtml = btn.innerHTML;
    btn.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Attendere...';

    try {
      // 1. Richiedi opzioni al server
      const startResp = await fetch('/auth/passkey/login/start', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
      });
      if (!startResp.ok) throw new Error('Errore server');

      const data = await startResp.json() as { options: Record<string, unknown>; sessionId: string };

      // 2. Prompt biometrico browser
      const assertion = await SimpleWebAuthnBrowser.startAuthentication({ optionsJSON: data.options });

      // 3. Verifica sul server
      const verifyResp = await fetch('/auth/passkey/login/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ assertion, sessionId: data.sessionId }),
      });

      if (verifyResp.ok) {
        const result = await verifyResp.json() as { ok: boolean; redirect?: string };
        window.location.href = result.redirect || '/';
      } else {
        throw new Error('Autenticazione fallita');
      }
    } catch (err: unknown) {
      // NotAllowedError: utente ha cancellato il prompt, ignora silenziosamente
      if (err instanceof Error && err.name === 'NotAllowedError') return;

      console.error('Errore login passkey:', err);

      // Mostra errore nell'area dedicata
      if (errorDiv) {
        errorDiv.textContent = 'Impossibile autenticare con passkey. Usa email e password.';
        errorDiv.classList.remove('d-none');
      }
    } finally {
      btn.disabled = false;
      btn.innerHTML = originalHtml;
    }
  }
}
